/**
 * The takeoff altitude an operator last used, remembered in this browser.
 *
 * Every session started at 20 m, so an operator who always launches to 40 m
 * retyped it every time, and the one time they forgot the aircraft stopped
 * 20 m short among the trees. Keyed per user because a shared ground station
 * laptop is normal, and one person's habit is not another's.
 *
 * Browser storage can be missing or refuse writes (a private window, a full
 * quota, site data blocked), so every access is guarded and falls back to the
 * default rather than taking the command dock down.
 */

export const DEFAULT_TAKEOFF_ALT_M = 20;
export const MAX_TAKEOFF_ALT_M = 500;

const KEY_PREFIX = 'parallax.takeoffAltitudeM.';

export function isValidTakeoffAltitude(metres: number): boolean {
  return Number.isFinite(metres) && metres > 0 && metres <= MAX_TAKEOFF_ALT_M;
}

export function loadTakeoffAltitude(userId: string | null | undefined): string {
  if (!userId) return String(DEFAULT_TAKEOFF_ALT_M);
  try {
    const stored = window.localStorage.getItem(KEY_PREFIX + userId);
    if (stored !== null && isValidTakeoffAltitude(Number.parseFloat(stored))) return stored;
  } catch {
    // Storage unavailable: the default is a working answer.
  }
  return String(DEFAULT_TAKEOFF_ALT_M);
}

/** Only a value the server would accept is remembered, so a half-typed field is not. */
export function saveTakeoffAltitude(userId: string | null | undefined, value: string): void {
  if (!userId || !isValidTakeoffAltitude(Number.parseFloat(value))) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + userId, value.trim());
  } catch {
    // Not remembered this time; the field still works.
  }
}
